import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import '../styles.css';

function AllianceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [alliance, setAlliance] = useState(null);
  const [members, setMembers] = useState([]);
  const [liveStats, setLiveStats] = useState(null);
  const [levelInfo, setLevelInfo] = useState(null);
  const [unlockedPerks, setUnlockedPerks] = useState([]);
  const [allPerks, setAllPerks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [activityHistory, setActivityHistory] = useState([]);
  const [randomBg, setRandomBg] = useState(null);
  const canvasRef = useRef(null);

  const accountHash = localStorage.getItem('avalw_account_hash');

  // Random background images
  useEffect(() => {
    const bgImages = [
      '1.jpeg', '2.jpeg', '3.jpeg', '4.jpeg', '5.jpeg', '6.jpeg', '7.jpeg',
      '9.jpeg', '11.jpeg', '13.jpeg', '15.jpeg', '16.jpeg', '17.jpeg',
      '18.jpeg', '19.jpeg', '20.jpeg', '22.jpeg', '24.jpeg', '25.jpeg',
      '26.jpeg', '27.jpeg', '28.jpeg', '29.jpeg'
    ];
    const randomImg = bgImages[Math.floor(Math.random() * bgImages.length)];
    setRandomBg(`https://cronos.avalw.com/media/${randomImg}`);
  }, []);

  useEffect(() => {
    fetchAllianceDetails();
    const interval = setInterval(fetchAllianceDetails, 10000);
    return () => clearInterval(interval);
  }, [id]);

  // Activity graph animation - track live network power
  useEffect(() => {
    if (!liveStats) return;
    // Track total live power (TF) - changes when workers come online/offline
    const livePower = liveStats.total_live_power || 0;
    setActivityHistory(prev => {
      const newHistory = [...prev, livePower];
      if (newHistory.length > 30) newHistory.shift();
      return newHistory;
    });
  }, [liveStats]);

  // Network visualization canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !members.length) return;

    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);

    const onlineMembers = members.filter(m => m.worker_status === 'online');
    const particles = [];
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    // Create particles for online members
    onlineMembers.forEach((m, i) => {
      const angle = (i / onlineMembers.length) * Math.PI * 2;
      const radius = 60 + Math.random() * 40;
      particles.push({
        x: centerX + Math.cos(angle) * radius,
        y: centerY + Math.sin(angle) * radius,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        size: 3 + Math.random() * 3,
        alpha: 0.5 + Math.random() * 0.5
      });
    });

    let animationId;
    const animate = () => {
      ctx.clearRect(0, 0, rect.width, rect.height);

      // Draw connections
      ctx.strokeStyle = 'rgba(139, 92, 246, 0.15)';
      ctx.lineWidth = 1;
      particles.forEach((p, i) => {
        particles.forEach((p2, j) => {
          if (i < j) {
            const dist = Math.hypot(p.x - p2.x, p.y - p2.y);
            if (dist < 80) {
              ctx.beginPath();
              ctx.moveTo(p.x, p.y);
              ctx.lineTo(p2.x, p2.y);
              ctx.stroke();
            }
          }
        });
      });

      // Draw center hub
      const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 20);
      gradient.addColorStop(0, 'rgba(139, 92, 246, 0.8)');
      gradient.addColorStop(1, 'rgba(139, 92, 246, 0)');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, 20, 0, Math.PI * 2);
      ctx.fill();

      // Draw and update particles
      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;

        // Bounce off edges
        if (p.x < 10 || p.x > rect.width - 10) p.vx *= -1;
        if (p.y < 10 || p.y > rect.height - 10) p.vy *= -1;

        // Draw particle
        ctx.fillStyle = `rgba(139, 92, 246, ${p.alpha})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();

        // Draw glow
        const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 3);
        glow.addColorStop(0, `rgba(139, 92, 246, ${p.alpha * 0.3})`);
        glow.addColorStop(1, 'rgba(139, 92, 246, 0)');
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 3, 0, Math.PI * 2);
        ctx.fill();
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(animationId);
  }, [members]);

  const fetchAllianceDetails = async () => {
    try {
      const response = await fetch(`/api/alliances/${id}`);
      const data = await response.json();
      if (data.success) {
        setAlliance(data.alliance);
        setMembers(data.members);
        setLiveStats(data.live_stats);
        setLevelInfo(data.level_info);
        setUnlockedPerks(data.unlocked_perks || []);
        setAllPerks(data.all_perks || []);
      } else {
        setError(data.error || 'Alliance not found');
      }
    } catch (err) {
      setError('Failed to load alliance');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!accountHash) { navigate('/'); return; }
    setActionLoading(true);
    try {
      const response = await fetch(`/api/alliances/${id}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_hash: accountHash })
      });
      const data = await response.json();
      if (data.success) fetchAllianceDetails();
      else alert(data.error || 'Failed to join');
    } catch (err) { alert('Failed to join alliance'); }
    finally { setActionLoading(false); }
  };

  const handleLeave = async () => {
    if (!window.confirm('Leave this alliance?')) return;
    setActionLoading(true);
    try {
      const response = await fetch(`/api/alliances/${id}/leave`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_hash: accountHash })
      });
      const data = await response.json();
      if (data.success) navigate('/alliances');
      else alert(data.error || 'Failed to leave');
    } catch (err) { alert('Failed to leave'); }
    finally { setActionLoading(false); }
  };

  const handleKick = async (targetHash) => {
    if (!window.confirm('Kick this member?')) return;
    setActionLoading(true);
    try {
      const response = await fetch(`/api/alliances/${id}/kick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_hash: accountHash, target_hash: targetHash })
      });
      const data = await response.json();
      if (data.success) fetchAllianceDetails();
      else alert(data.error || 'Failed');
    } catch (err) { alert('Failed'); }
    finally { setActionLoading(false); }
  };

  const handleTransfer = async (newLeaderHash) => {
    if (!window.confirm('Transfer leadership?')) return;
    setActionLoading(true);
    try {
      const response = await fetch(`/api/alliances/${id}/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_hash: accountHash, new_leader_hash: newLeaderHash })
      });
      const data = await response.json();
      if (data.success) fetchAllianceDetails();
      else alert(data.error || 'Failed');
    } catch (err) { alert('Failed'); }
    finally { setActionLoading(false); }
  };

  const openEditModal = () => {
    setEditForm({
      name: alliance.name,
      description: alliance.description || '',
      banner_color: alliance.banner_color || '#8b5cf6',
      image_url: alliance.image_url || '',
      background_url: alliance.background_url || '',
      min_level: alliance.join_requirement?.min_level || 1,
      min_serves: alliance.join_requirement?.min_serves || 0,
      open: alliance.join_requirement?.open !== false
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`/api/alliances/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account_hash: accountHash,
          name: editForm.name,
          description: editForm.description,
          banner_color: editForm.banner_color,
          image_url: editForm.image_url,
          background_url: editForm.background_url,
          join_requirement: {
            min_level: parseInt(editForm.min_level) || 1,
            min_serves: parseInt(editForm.min_serves) || 0,
            open: editForm.open
          }
        })
      });
      const data = await response.json();
      if (data.success) {
        setShowEditModal(false);
        fetchAllianceDetails();
      } else {
        alert(data.error || 'Failed to update');
      }
    } catch (err) { alert('Failed to save'); }
    finally { setActionLoading(false); }
  };

  const formatNum = (num) => {
    if (num === null || num === undefined) return '0';
    const n = parseFloat(num);
    if (isNaN(n)) return '0';
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    if (Number.isInteger(n)) return n.toString();
    if (n >= 100) return Math.round(n).toString();
    return n.toFixed(1);
  };

  // Perk icon helper
  const getPerkIcon = (unlock) => {
    const icons = {
      start: '*',
      banner_color: '#',
      avatar_frame: '[ ]',
      extended_bio: 'T',
      website_link: '@',
      social_links: '~',
      logo_upload: 'img',
      profile_theme: 'O',
      showcase_section: '++',
      custom_background: 'bg',
      animated_badge: '*',
      custom_colors: 'c',
      legendary_border: '!',
      diamond_effects: '<>',
      mythic_border: '{ }',
      beta_access: 'B',
      featured_article: 'A',
      governance_vote: 'V',
      custom_subdomain: '.com',
      dedicated_endpoint: '///'
    };
    return icons[unlock] || '?';
  };

  // Live Activity Graph Component
  const ActivityGraph = ({ data, color = '#8b5cf6' }) => {
    const max = Math.max(...data, 1);
    const points = data.map((v, i) => {
      const x = (i / (data.length - 1)) * 100;
      const y = 100 - (v / max) * 80;
      return `${x},${y}`;
    }).join(' ');

    return (
      <svg viewBox="0 0 100 100" className="adv-activity-graph" preserveAspectRatio="none">
        <defs>
          <linearGradient id="graphGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={`0,100 ${points} 100,100`} fill="url(#graphGradient)" />
        <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {data.length > 0 && (
          <circle cx="100" cy={100 - (data[data.length - 1] / max) * 80} r="3" fill={color} className="adv-pulse-dot" />
        )}
      </svg>
    );
  };

  // Animated Stat Card Component
  const StatCard = ({ value, label, icon, color, max, subValue }) => {
    const pct = Math.min((parseFloat(value) / max) * 100, 100);

    return (
      <div className="adv-stat-card">
        <div className="adv-stat-icon" style={{ '--clr': color }}>
          {icon}
        </div>
        <div className="adv-stat-content">
          <div className="adv-stat-value" style={{ color }}>
            {formatNum(value)}
            {subValue && <span className="adv-stat-sub">{subValue}</span>}
          </div>
          <div className="adv-stat-label">{label}</div>
          <div className="adv-stat-bar">
            <div className="adv-stat-bar-fill" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}, ${color}88)` }}>
              <div className="adv-stat-bar-glow" style={{ background: color }}></div>
            </div>
          </div>
        </div>
        <div className="adv-stat-ring" style={{ '--pct': pct, '--clr': color }}>
          <svg viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="16" className="adv-ring-bg" />
            <circle cx="18" cy="18" r="16" className="adv-ring-fill" />
          </svg>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="adv-page">
        <div className="adv-loading">
          <div className="adv-loader">
            <div className="adv-loader-ring"></div>
            <div className="adv-loader-ring"></div>
            <div className="adv-loader-ring"></div>
          </div>
          <p>Loading Alliance...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="adv-page">
        <div className="adv-error">
          <div className="adv-error-icon">!</div>
          <h2>{error}</h2>
          <Link to="/alliances" className="adv-btn">Back to Alliances</Link>
        </div>
      </div>
    );
  }

  const isLeader = alliance?.leader_hash === accountHash;
  const isMember = members.some(m => m.full_hash === accountHash);
  const coresOnline = liveStats?.total_cores_online || 0;
  const workersOnline = liveStats?.online_count || 0;
  const coreHours = liveStats?.total_core_hours || 0;
  const co2Saved = alliance?.co2_kg || 0;
  const livePower = liveStats?.total_live_power || 0;

  const totalRam = liveStats?.total_ram_gb || 0;

  return (
    <div className="adv-page">
      {/* Background */}
      {randomBg && (
        <div className="adv-bg" style={{ backgroundImage: `url(${randomBg})` }}></div>
      )}

      {/* Nav */}
      <nav className="adv-nav">
        <Link to="/" className="adv-logo">
          <span className="adv-logo-pulse"></span>
          <span className="adv-logo-text">AVALW</span>
        </Link>
        <div className="adv-nav-links">
          <Link to="/">Home</Link>
          <Link to="/alliances">Alliances</Link>
          <Link to="/rewards">Rewards</Link>
          {accountHash && <Link to="/dashboard">Dashboard</Link>}
        </div>
      </nav>

      {/* Hero Section */}
      <header className="adv-hero">
        <div className="adv-container">
          <div className="adv-hero-grid">
            {/* Left - Alliance Info */}
            <div className="adv-hero-info">
              <div className="adv-hero-badge" style={{ '--clr': alliance.banner_color || '#8b5cf6' }}>
                {alliance.image_url ? (
                  <img src={alliance.image_url} alt={alliance.name} />
                ) : (
                  <span className="adv-hero-level">Lv.{alliance.level}</span>
                )}
                <div className="adv-hero-badge-ring"></div>
              </div>
              <div className="adv-hero-text">
                <h1 className="adv-hero-title">{alliance.name}</h1>
                <p className="adv-hero-meta">
                  <span className="adv-hero-leader">Leader: <strong>{alliance.leader_name}</strong></span>
                  <span className="adv-hero-count">{members.length}/100 members</span>
                </p>
                <p className="adv-hero-desc">{alliance.description || 'No description'}</p>
                <div className="adv-hero-level-badge" style={{ '--clr': alliance.banner_color || '#8b5cf6' }}>
                  <span className="adv-hero-level-num">Lv. {levelInfo?.current || alliance.level || 1}</span>
                  <div className="adv-hero-level-progress">
                    <div className="adv-hero-level-bar">
                      <div
                        className="adv-hero-level-fill"
                        style={{
                          width: `${levelInfo?.progress || 0}%`,
                          background: alliance.banner_color || '#8b5cf6'
                        }}
                      />
                    </div>
                    <span className="adv-hero-level-co2">
                      {(() => {
                        const co2 = levelInfo?.co2_current || 0;
                        return co2 >= 1 ? `${formatNum(co2)} kg` : `${Math.round(co2 * 1000)} g`;
                      })()} CO2
                    </span>
                  </div>
                </div>
                {alliance.join_requirement && !alliance.join_requirement.open && (
                  <div className="adv-hero-req">
                    <span>Level {alliance.join_requirement.min_level}+</span>
                    {alliance.join_requirement.min_serves > 0 && <span>{alliance.join_requirement.min_serves}+ serves</span>}
                  </div>
                )}
              </div>
            </div>

            {/* Right - Network Visualization */}
            <div className="adv-hero-visual">
              <canvas ref={canvasRef} className="adv-network-canvas"></canvas>
              <div className="adv-network-stats">
                <div className="adv-live-indicator">
                  <span className="adv-heartbeat"></span>
                  <span className="adv-live-text">LIVE</span>
                </div>
                <div className="adv-live-power">{formatNum(livePower)} TF</div>
                <div className="adv-live-label">Network Power</div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="adv-hero-actions">
            <button className="adv-btn adv-btn-share" onClick={() => {
              const url = window.location.href;
              if (navigator.share) {
                navigator.share({ title: alliance.name, text: `Join ${alliance.name} on AVALW!`, url });
              } else {
                navigator.clipboard.writeText(url);
                alert('Link copied to clipboard!');
              }
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
              Share
            </button>
            {isLeader && (
              <button className="adv-btn adv-btn-secondary" onClick={openEditModal}>
                <span className="adv-btn-icon">*</span> Edit Alliance
              </button>
            )}
            {!accountHash ? (
              <Link to="/" className="adv-btn adv-btn-primary">Join Network</Link>
            ) : isMember ? (
              <button className="adv-btn adv-btn-danger" onClick={handleLeave} disabled={actionLoading}>Leave Alliance</button>
            ) : (
              <button className="adv-btn adv-btn-primary" onClick={handleJoin} disabled={actionLoading || alliance.member_count >= 100}>
                {alliance.member_count >= 100 ? 'Alliance Full' : 'Join Alliance'}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Stats Dashboard - Ultra Advanced */}
      <section className="adv-stats">
        <div className="adv-container">
          <div className="adv-stats-header">
            <h2>Alliance Statistics</h2>
            <div className="adv-stats-live">
              <span className="adv-heartbeat-sm"></span> Real-time
            </div>
          </div>

          <div className="adv-mega-stats">
            {/* Primary Stats Row */}
            <div className="adv-mega-row">
              <div className="adv-mega-card adv-mega-primary" style={{'--accent': '#8b5cf6'}}>
                <div className="adv-mega-glow"></div>
                <div className="adv-mega-content">
                  <div className="adv-mega-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="4" y="4" width="16" height="16" rx="2"/>
                      <rect x="9" y="9" width="6" height="6"/>
                      <line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/>
                      <line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/>
                    </svg>
                    <div className="adv-mega-pulse"></div>
                  </div>
                  <div className="adv-mega-data">
                    <span className="adv-mega-value">{formatNum(coresOnline)}</span>
                    <span className="adv-mega-label">Cores Online</span>
                    <div className="adv-mega-bar">
                      <div className="adv-mega-fill" style={{width: `${Math.min(coresOnline/500*100, 100)}%`}}></div>
                    </div>
                  </div>
                </div>
                <div className="adv-mega-particles">
                  <span></span><span></span><span></span>
                </div>
              </div>

              <div className="adv-mega-card adv-mega-primary" style={{'--accent': '#3b82f6'}}>
                <div className="adv-mega-glow"></div>
                <div className="adv-mega-content">
                  <div className="adv-mega-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                    <div className="adv-mega-pulse"></div>
                  </div>
                  <div className="adv-mega-data">
                    <span className="adv-mega-value">{workersOnline}</span>
                    <span className="adv-mega-label">Contributors Online</span>
                    <div className="adv-mega-bar">
                      <div className="adv-mega-fill" style={{width: `${Math.min(workersOnline/100*100, 100)}%`}}></div>
                    </div>
                  </div>
                </div>
                <div className="adv-mega-particles">
                  <span></span><span></span><span></span>
                </div>
              </div>
            </div>

            {/* Secondary Stats Row */}
            <div className="adv-mega-row adv-mega-row-3">
              <div className="adv-mega-card" style={{'--accent': '#22c55e'}}>
                <div className="adv-mega-glow"></div>
                <div className="adv-mega-content">
                  <div className="adv-mega-icon-sm">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </svg>
                  </div>
                  <div className="adv-mega-data">
                    <span className="adv-mega-value">{formatNum(coreHours)}</span>
                    <span className="adv-mega-label">Core-Hours</span>
                  </div>
                </div>
              </div>

              <div className="adv-mega-card" style={{'--accent': '#14b8a6'}}>
                <div className="adv-mega-glow"></div>
                <div className="adv-mega-content">
                  <div className="adv-mega-icon-sm">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M12 22c4.97 0 9-4.03 9-9-4.97 0-9 4.03-9 9z"/>
                      <path d="M3 13c0 4.97 4.03 9 9 9 0-4.97-4.03-9-9-9z"/>
                      <circle cx="12" cy="8" r="5"/>
                    </svg>
                  </div>
                  <div className="adv-mega-data">
                    <span className="adv-mega-value">{(Math.round(((coreHours * 4) / 1000 + co2Saved) * 10) / 10).toFixed(1)}<small>kg</small></span>
                    <span className="adv-mega-label">CO2 Saved</span>
                  </div>
                </div>
              </div>

              <div className="adv-mega-card" style={{'--accent': '#f59e0b'}}>
                <div className="adv-mega-glow"></div>
                <div className="adv-mega-content">
                  <div className="adv-mega-icon-sm">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="2" y="6" width="20" height="12" rx="2"/>
                      <line x1="6" y1="10" x2="6" y2="14"/>
                      <line x1="10" y1="10" x2="10" y2="14"/>
                      <line x1="14" y1="10" x2="14" y2="14"/>
                      <line x1="18" y1="10" x2="18" y2="14"/>
                    </svg>
                  </div>
                  <div className="adv-mega-data">
                    <span className="adv-mega-value">{formatNum(totalRam)}<small>GB</small></span>
                    <span className="adv-mega-label">Total RAM</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Eco Impact Section - Green Algorithms Formula */}
          {(() => {
            // Green Algorithms formula: ~4g CO2 per core-hour (12W TDP × 400g/kWh × PUE 1.0)
            // Source: https://www.green-algorithms.org/
            const co2FromCoreHours = (coreHours * 4) / 1000; // kg (4g per core-hour)
            const totalCO2 = co2FromCoreHours + co2Saved;

            // Equivalencies based on EPA & scientific sources
            const treesEquiv = Math.round(totalCO2 / 0.06 * 10) / 10; // 60kg per tree over 10 years = 6kg/year = 0.06kg/year for seedling equivalent
            const carKm = Math.round(totalCO2 / 0.12 * 10) / 10; // 120g CO2 per km (EU average)
            const smartphoneCharges = Math.round(totalCO2 / 0.0124); // 12.4g per charge (EPA)
            const netflixHours = Math.round(totalCO2 / 0.055 * 10) / 10; // 55g per hour (Carbon Trust)
            const googleSearches = Math.round(totalCO2 / 0.0002); // 0.2g per search (Google)
            const coffeeCups = Math.round(totalCO2 / 0.05 * 10) / 10; // 50g per espresso
            const ledHours = Math.round(totalCO2 / 0.004); // 4g per hour (7W LED)

            return (
              <div className="adv-eco-section">
                <div className="adv-eco-header">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="adv-eco-icon">
                    <path d="M12 22c4.97 0 9-4.03 9-9-4.97 0-9 4.03-9 9zM5.6 10.25c0 1.38 1.12 2.5 2.5 2.5.53 0 1.01-.16 1.42-.44l-.02.19c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5l-.02-.19c.4.28.89.44 1.42.44 1.38 0 2.5-1.12 2.5-2.5 0-1-.59-1.85-1.43-2.25.84-.4 1.43-1.25 1.43-2.25 0-1.38-1.12-2.5-2.5-2.5-.53 0-1.01.16-1.42.44l.02-.19C14.5 2.12 13.38 1 12 1S9.5 2.12 9.5 3.5l.02.19c-.4-.28-.89-.44-1.42-.44-1.38 0-2.5 1.12-2.5 2.5 0 1 .59 1.85 1.43 2.25-.84.4-1.43 1.25-1.43 2.25zM12 5.5c1.38 0 2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5S9.5 9.38 9.5 8s1.12-2.5 2.5-2.5zM3 13c0 4.97 4.03 9 9 9 0-4.97-4.03-9-9-9z"/>
                  </svg>
                  <h3>Eco Impact</h3>
                  <div className="adv-eco-sources">
                    <a href="https://www.green-algorithms.org/" target="_blank" rel="noopener noreferrer" className="adv-eco-source">Green Algorithms</a>
                    <span className="adv-eco-source-sep">•</span>
                    <a href="https://www.epa.gov/energy/greenhouse-gas-equivalencies-calculator-calculations-and-references" target="_blank" rel="noopener noreferrer" className="adv-eco-source">EPA Calculator</a>
                  </div>
                </div>
                <div className="adv-eco-content">
                  <div className="adv-eco-left">
                    <div className="adv-eco-main">
                      <span className="adv-eco-value">{totalCO2 < 1 ? (totalCO2 * 1000).toFixed(0) : totalCO2.toFixed(2)}</span>
                      <span className="adv-eco-unit">{totalCO2 < 1 ? 'g' : 'kg'} CO2 Saved</span>
                    </div>
                    <div className="adv-eco-grid">
                      {/* Row 1 */}
                      <div className="adv-eco-item">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 22c4.97 0 9-4.03 9-9-4.97 0-9 4.03-9 9zM5.6 10.25c0 1.38 1.12 2.5 2.5 2.5.53 0 1.01-.16 1.42-.44l-.02.19c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5l-.02-.19c.4.28.89.44 1.42.44 1.38 0 2.5-1.12 2.5-2.5 0-1-.59-1.85-1.43-2.25.84-.4 1.43-1.25 1.43-2.25 0-1.38-1.12-2.5-2.5-2.5-.53 0-1.01.16-1.42.44l.02-.19C14.5 2.12 13.38 1 12 1S9.5 2.12 9.5 3.5l.02.19c-.4-.28-.89-.44-1.42-.44-1.38 0-2.5 1.12-2.5 2.5 0 1 .59 1.85 1.43 2.25-.84.4-1.43 1.25-1.43 2.25zM12 5.5c1.38 0 2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5S9.5 9.38 9.5 8s1.12-2.5 2.5-2.5zM3 13c0 4.97 4.03 9 9 9 0-4.97-4.03-9-9-9z"/>
                        </svg>
                        <span className="adv-eco-num">{treesEquiv}</span>
                        <span className="adv-eco-label">tree seedlings</span>
                      </div>
                      <div className="adv-eco-item">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
                        </svg>
                        <span className="adv-eco-num">{formatNum(carKm)}</span>
                        <span className="adv-eco-label">km not driven</span>
                      </div>
                      <div className="adv-eco-item">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z"/>
                        </svg>
                        <span className="adv-eco-num">{formatNum(smartphoneCharges)}</span>
                        <span className="adv-eco-label">phone charges</span>
                      </div>
                      {/* Row 2 */}
                      <div className="adv-eco-item">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M4 6h16v2H4zm2-4h12v2H6zm14 8H4c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2v-8c0-1.1-.9-2-2-2zm-8 11c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm2-4l-3-2v4l3-2z"/>
                        </svg>
                        <span className="adv-eco-num">{formatNum(netflixHours)}</span>
                        <span className="adv-eco-label">streaming hrs</span>
                      </div>
                      <div className="adv-eco-item">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                        </svg>
                        <span className="adv-eco-num">{formatNum(googleSearches)}</span>
                        <span className="adv-eco-label">searches</span>
                      </div>
                      <div className="adv-eco-item">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M2 21h18v-2H2v2zm18-10H2v8h18v-8zm0-8H2v6h18V3z M4 5h14v2H4V5zm0 8h14v4H4v-4z"/>
                        </svg>
                        <span className="adv-eco-num">{formatNum(coffeeCups)}</span>
                        <span className="adv-eco-label">espressos</span>
                      </div>
                      {/* Row 3 */}
                      <div className="adv-eco-item adv-eco-item-wide">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9 21c0 .5.4 1 1 1h4c.6 0 1-.5 1-1v-1H9v1zm3-19C8.1 2 5 5.1 5 9c0 2.4 1.2 4.5 3 5.7V17c0 .5.4 1 1 1h6c.6 0 1-.5 1-1v-2.3c1.8-1.3 3-3.4 3-5.7 0-3.9-3.1-7-7-7z"/>
                        </svg>
                        <span className="adv-eco-num">{formatNum(ledHours)}</span>
                        <span className="adv-eco-label">LED bulb hours (7W)</span>
                      </div>
                    </div>
                  </div>
                  <div className="adv-eco-graph">
                    <ActivityGraph data={activityHistory.length > 1 ? activityHistory.map(v => v * 0.004) : [0, totalCO2 * 0.8, totalCO2]} color="#22c55e" />
                    <div className="adv-eco-graph-label">CO2 Savings Trend</div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Activity Graph */}
          <div className="adv-activity-section">
            <div className="adv-activity-header">
              <h3>Network Power</h3>
              <span className="adv-activity-time">Live • Updates every 10s</span>
            </div>
            <div className="adv-activity-chart">
              <ActivityGraph data={activityHistory.length > 1 ? activityHistory : [0, 0]} color="#8b5cf6" />
              <div className="adv-activity-labels">
                <span>0</span>
                <span>{formatNum(Math.max(...activityHistory, 1))} TF</span>
              </div>
            </div>
            <div className="adv-activity-info">
              <span className="adv-activity-current">Current: {formatNum(livePower)} TF • {workersOnline} workers</span>
              <span className="adv-activity-total">Total: {formatNum(coreHours)} core-hours</span>
            </div>
          </div>
        </div>
      </section>

      {/* CO2 Level Progress - Enhanced */}
      {(() => {
        // Use CO2-based level info
        const currentCO2 = levelInfo?.co2_current ?? alliance?.co2_saved_kg ?? 0;
        const co2Needed = levelInfo?.co2_needed ?? 50;
        const currentLevel = levelInfo?.current ?? alliance?.level ?? 1;
        const progress = levelInfo?.progress ?? Math.min(100, Math.round((currentCO2 / co2Needed) * 100));
        const isMaxLevel = currentLevel >= 60;

        return (
          <section className="adv-xp">
            <div className="adv-container">
              <div className="adv-xp-card">
                <div className="adv-xp-info">
                  <div className="adv-xp-level" style={{ '--clr': alliance.banner_color || '#8b5cf6' }}>
                    <span className="adv-xp-level-num">{currentLevel}</span>
                    <span className="adv-xp-level-label">LEVEL</span>
                  </div>
                  <div className="adv-xp-details">
                    <div className="adv-xp-progress-text">
                      <span className="adv-xp-current">{formatNum(currentCO2)}</span>
                      <span className="adv-xp-separator">/</span>
                      <span className="adv-xp-needed">{formatNum(co2Needed)} kg CO2</span>
                    </div>
                    <div className="adv-xp-bar">
                      <div className="adv-xp-fill" style={{ width: `${progress}%`, '--clr': alliance.banner_color || '#8b5cf6' }}>
                        <div className="adv-xp-glow"></div>
                      </div>
                    </div>
                    <div className="adv-xp-to-next">
                      {isMaxLevel ? 'MAX LEVEL REACHED!' : `${formatNum(co2Needed - currentCO2)} kg CO2 to next level`}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Alliance Perks Section */}
      {allPerks.length > 0 && (
        <section className="adv-perks">
          <div className="adv-container">
            <div className="adv-section-header">
              <h2>Alliance Perks</h2>
              <span className="adv-perks-count">{unlockedPerks.length}/{allPerks.length} Unlocked</span>
            </div>
            <div className="adv-perks-grid">
              {allPerks.map((perk) => (
                <div
                  key={perk.level}
                  className={`adv-perk-card ${perk.unlocked ? 'unlocked' : 'locked'} ${perk.level >= 50 ? 'legendary' : ''}`}
                  style={{ '--clr': alliance.banner_color || '#8b5cf6' }}
                >
                  <div className="adv-perk-level">Lv.{perk.level}</div>
                  <div className="adv-perk-icon">
                    {perk.unlocked ? (
                      getPerkIcon(perk.unlock)
                    ) : (
                      <span className="adv-perk-lock">?</span>
                    )}
                  </div>
                  <div className="adv-perk-name">{perk.name}</div>
                  <div className="adv-perk-desc">{perk.description}</div>
                  {!perk.unlocked && (
                    <div className="adv-perk-locked-overlay">
                      <span>LOCKED</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Members */}
      <section className="adv-members">
        <div className="adv-container">
          <div className="adv-section-header">
            <h2>Members</h2>
            <div className="adv-member-counts">
              <span className="adv-online-count">
                <span className="adv-pulse-dot-sm"></span>
                {liveStats?.online_count || 0} online
              </span>
              <span className="adv-total-count">{members.length}/100 total</span>
            </div>
          </div>

          <div className="adv-members-grid">
            {members.map((m, i) => (
              <div key={m.id} className={`adv-member-card ${m.worker_status === 'online' ? 'online' : ''}`}>
                <div className="adv-member-rank">#{i + 1}</div>
                <div className="adv-member-avatar" style={{ '--clr': alliance.banner_color || '#8b5cf6' }}>
                  {m.display_name.charAt(0).toUpperCase()}
                  {m.worker_status === 'online' && <span className="adv-member-online-ring"></span>}
                </div>
                <div className="adv-member-info">
                  <div className="adv-member-name">
                    {m.is_leader && <span className="adv-leader-crown">*</span>}
                    {m.display_name}
                  </div>
                  <div className={`adv-member-status ${m.worker_status}`}>
                    {m.worker_status === 'online' ? (
                      <>
                        <span className="adv-status-pulse"></span>
                        Online
                      </>
                    ) : (
                      'Offline'
                    )}
                  </div>
                </div>
                {isLeader && !m.is_leader && (
                  <div className="adv-member-actions">
                    <button onClick={() => handleTransfer(m.full_hash)} title="Transfer Leadership" className="adv-action-btn transfer">T</button>
                    <button onClick={() => handleKick(m.full_hash)} title="Kick Member" className="adv-action-btn kick">X</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="adv-footer">
        <p>AVALW Worker Network</p>
      </footer>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="adv-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="adv-modal" onClick={e => e.stopPropagation()}>
            <div className="adv-modal-header">
              <h2>Edit Alliance</h2>
              <button className="adv-modal-close" onClick={() => setShowEditModal(false)}>X</button>
            </div>
            <div className="adv-modal-body">
              <div className="adv-form-group">
                <label>Alliance Name</label>
                <input type="text" value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} maxLength={30} />
              </div>
              <div className="adv-form-group">
                <label>Description</label>
                <textarea value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} maxLength={500} rows={3} />
              </div>
              <div className="adv-form-group">
                <label>Image URL</label>
                <input type="text" value={editForm.image_url} onChange={e => setEditForm({...editForm, image_url: e.target.value})} placeholder="https://..." />
                {editForm.image_url && <img src={editForm.image_url} alt="Preview" className="adv-preview" onError={e => e.target.style.display='none'} />}
              </div>
              <div className="adv-form-group">
                <label>Background URL</label>
                <input type="text" value={editForm.background_url} onChange={e => setEditForm({...editForm, background_url: e.target.value})} placeholder="https://..." />
                {editForm.background_url && <img src={editForm.background_url} alt="BG" className="adv-preview-bg" onError={e => e.target.style.display='none'} />}
              </div>
              <div className="adv-form-group">
                <label>Banner Color</label>
                <div className="adv-color-row">
                  <input type="color" value={editForm.banner_color} onChange={e => setEditForm({...editForm, banner_color: e.target.value})} />
                  <span>{editForm.banner_color}</span>
                </div>
              </div>
              <div className="adv-form-divider">Join Requirements</div>
              <div className="adv-form-row">
                <div className="adv-form-group">
                  <label>Min Level</label>
                  <input type="number" min="1" max="50" value={editForm.min_level} onChange={e => setEditForm({...editForm, min_level: e.target.value})} />
                </div>
                <div className="adv-form-group">
                  <label>Min Serves</label>
                  <input type="number" min="0" value={editForm.min_serves} onChange={e => setEditForm({...editForm, min_serves: e.target.value})} />
                </div>
              </div>
              <div className="adv-form-group">
                <label className="adv-checkbox">
                  <input type="checkbox" checked={editForm.open} onChange={e => setEditForm({...editForm, open: e.target.checked})} />
                  <span>Open to everyone</span>
                </label>
              </div>
            </div>
            <div className="adv-modal-footer">
              <button className="adv-btn" onClick={() => setShowEditModal(false)}>Cancel</button>
              <button className="adv-btn adv-btn-primary" onClick={handleSaveEdit} disabled={actionLoading}>
                {actionLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AllianceDetail;
